import { format } from 'date-fns';
import { sizeValue } from '../components/overlay/tracksModel';

/**
 * Words and numbers of the video count report, as the backend report wrote them (video_detection_report.py): times,
 * dates in English, the size range kept, the strip unit. pdfText keeps a person's text printable in the report font.
 */

const two = n => String(n).padStart(2, '0');

/** Video time as the page shows it: m:ss.s, h:mm:ss.s from one hour; `decimals` 0 gives m:ss (whole seconds, rounded). */
export const clock = (seconds, decimals = 1) => {
	const k = 10 ** decimals;
	// round first, so 59.96 s is 1:00.0, not 0:60.0
	const s = Math.round(Math.max(0, seconds || 0) * k) / k;
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	const sec = (s - 3600 * h - 60 * m)
		.toFixed(decimals)
		.padStart(decimals ? 3 + decimals : 2, '0');
	return h >= 1 ? `${h}:${two(m)}:${sec}` : `${m}:${sec}`;
};

/** "23 Oct 2026, 14:05": the top bar of every page. */
export const reportDate = d => format(d, 'd MMM yyyy, HH:mm');

/** "22 Oct, 16:40": when the analysis ran. */
export const runDate = d => format(d, 'd MMM, HH:mm');

/** "2026-10-23_1405": the file name's time. */
export const fileStamp = d => format(d, 'yyyy-MM-dd_HHmm');

/** "0.72 – 4.5 m", "up to 4.5 m" (no lower limit), "every size" (no range); each size as the size filter writes it. */
export const sizeRangeText = (range, unit) => {
	if (!range) return 'every size';
	const [lo, hi] = range;
	return lo > 0
		? `${sizeValue(lo, unit)} – ${sizeValue(hi, unit)} ${unit}`
		: `up to ${sizeValue(hi, unit)} ${unit}`;
};

/** The length of one strip bar: "second", "0.5 s", "2 s", "2 min". */
export const stripUnit = sec => {
	if (Math.abs(sec - 1) < 0.05) return 'second';
	if (sec >= 60 && sec % 60 === 0) return `${sec / 60} min`;
	return `${Number(sec.toFixed(2))} s`;
};

// tick steps of a time axis, in seconds
const TICK_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

/** Ticks of a video time axis from `start` to `end` (s): every 1/2/5/10/15/30 s or minutes, at most six or so. */
export const timeTicks = (start, end) => {
	const span = Math.max(end - start, 1e-6);
	const step = TICK_STEPS.find(t => span / t <= 6) ?? TICK_STEPS[TICK_STEPS.length - 1];
	const out = [];
	for (let t = Math.ceil(start / step) * step; t <= end + 1e-6; t += step) out.push(t);
	return out;
};

/** "4K · 29.97 fps", "1080p · 30.00 fps". */
export const videoLabel = (height, fps) =>
	`${height >= 2160 ? '4K' : `${height}p`} · ${Number(fps).toFixed(2)} fps`;

// pictographs, flags, skin tones, variation selectors, joiners and keycaps: an emoji goes as a whole
const EMOJI =
	/\p{Extended_Pictographic}|\p{Regional_Indicator}|\p{Emoji_Modifier}|\u200d|\ufe0e|\ufe0f|\u20e3/u;

/**
 * `text` with only the characters the report font has (`has(codePoint)`): any other one becomes its plain form
 * ("ｆ" → "f", NFKC) when the font has that, else "?"; an emoji goes. The project title on the page stays as typed.
 */
export const pdfText = (text, has) =>
	[...String(text ?? '').normalize('NFC')]
		.map(ch => {
			if (has(ch.codePointAt(0))) return ch;
			if (EMOJI.test(ch)) return '';
			const plain = ch.normalize('NFKC');
			return plain !== ch && [...plain].every(c => has(c.codePointAt(0))) ? plain : '?';
		})
		.join('')
		.replace(/\s{2,}/g, ' ')
		.trim();
